import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import { StaticElement } from "@/components/slides/editor/StaticElement";
import { LineRenderer } from "@/components/slides/editor/renderers/LineRenderer";
import { questionTemplates } from "@/lib/constants/questionDefaults";
import { type LineConfig, shapeSizeMap } from "@/lib/hooks/slides/editor/useAddShapeElement";
import { textTypeMap } from "@/lib/hooks/slides/editor/useAddTextElement";
import { useAppSelector } from "@/store/hooks";
import type { ShapeType, SlideElement, TextStyle } from "@ai-teach/types";
import { DragOverlay, type Modifier } from "@dnd-kit/core";
import { useEffect, useState } from "react";
import type { DragData } from "../types";

interface DndOverlayProps {
  draggedItem: DragData | null;
}

// dnd-kit anchors the overlay to the source node — re-center it on the cursor to match the drop.
const snapCenterToCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (!draggingNodeRect || !activatorEvent) return transform;
  const evt = activatorEvent as PointerEvent;
  if (typeof evt.clientX !== "number") return transform;
  const offsetX = evt.clientX - draggingNodeRect.left;
  const offsetY = evt.clientY - draggingNodeRect.top;
  return {
    ...transform,
    x: transform.x + offsetX - draggingNodeRect.width / 2,
    y: transform.y + offsetY - draggingNodeRect.height / 2,
  };
};

// Mirror of the sizing in useFreeformHandlers.addImage — fit the natural size into a 300px box.
const IMAGE_MAX_DIMENSION = 300;
function fitImageSize(naturalWidth: number, naturalHeight: number) {
  const aspectRatio = naturalWidth / naturalHeight;
  if (naturalWidth > naturalHeight) {
    const width = Math.min(naturalWidth, IMAGE_MAX_DIMENSION);
    return { width, height: width / aspectRatio };
  }
  const height = Math.min(naturalHeight, IMAGE_MAX_DIMENSION);
  return { width: height * aspectRatio, height };
}

// Render the real element the drop will create, at its true footprint scaled to the screen.
function ScaledStatic({ element, scale }: { element: SlideElement; scale: number }) {
  const w = element.size.width;
  const h = element.size.height;
  return (
    <div className="opacity-90" style={{ width: w * scale, height: h * scale }}>
      <div
        className="relative"
        style={{ width: w, height: h, transform: `scale(${scale})`, transformOrigin: "top left" }}
      >
        <StaticElement element={element} />
      </div>
    </div>
  );
}

export function DndOverlay({ draggedItem }: DndOverlayProps) {
  const canvasSize = useAppSelector((state) => state.slideEditor.present.canvasSize);
  const { canvasRef } = useSlideEditorContext();

  // Measure the canvas's real rendered scale (redux `scale` stays 1); the overlay is on-screen px.
  const rect = canvasRef.current?.getBoundingClientRect();
  const unscaledW = canvasSize.height * canvasSize.aspectRatio;
  const scale = rect?.width && unscaledW ? rect.width / unscaledW : 1;

  // The dropped image is sized from its natural dimensions — load them so the preview matches.
  const imageSrc = draggedItem?.type === "image" ? draggedItem.media?.src : undefined;
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    setImageSize(null);
    if (!imageSrc) return;
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => setImageSize(fitImageSize(img.naturalWidth, img.naturalHeight));
  }, [imageSrc]);

  const preview = buildPreviewElement(draggedItem, imageSize);

  return (
    <DragOverlay modifiers={[snapCenterToCursor]}>
      {preview && <ScaledStatic element={preview} scale={scale} />}

      {draggedItem?.type === "line" && (
        <div style={{ width: 200 * scale, height: 100 * scale }} className="opacity-90">
          <div
            className="relative"
            style={{
              width: 200,
              height: 100,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            <LineRenderer
              element={{
                id: draggedItem.id,
                startX: 0,
                startY: 50,
                endX: 100,
                endY: 50,
                stroke: "#000000",
                strokeWidth: 3,
                lineStyle: (draggedItem.data as LineConfig).lineStyle,
                startArrow: (draggedItem.data as LineConfig).startArrow,
                endArrow: (draggedItem.data as LineConfig).endArrow,
              }}
            />
          </div>
        </div>
      )}
    </DragOverlay>
  );
}

// Build the synthetic element for each draggable type — same shape/size/style the drop creates.
function buildPreviewElement(
  draggedItem: DragData | null,
  imageSize: { width: number; height: number } | null
): SlideElement | null {
  if (!draggedItem) return null;
  const noBorder = {
    lineStyle: "none" as const,
    stroke: "transparent",
    strokeWidth: 3,
    cornerRadius: 0,
  };

  switch (draggedItem.type) {
    case "shape":
      return {
        id: "overlay",
        type: "shape",
        shapeType: draggedItem.id as ShapeType,
        position: { x: 0, y: 0 },
        size: shapeSizeMap[draggedItem.id as ShapeType] ?? { width: 200, height: 200 },
        fill: "#000000",
        border: noBorder,
      } as unknown as SlideElement;

    case "text": {
      const config = textTypeMap[draggedItem.id];
      if (!config) return null;
      return {
        id: "overlay",
        type: "text",
        position: { x: 0, y: 0 },
        size: config.size,
        content: "Your text...",
        style: {
          color: "#000000",
          textAlign: "center",
          fontFamily: "System",
          fontStyle: "normal",
          textDecoration: "none",
          fontSize: config.fontSize,
          fontWeight: config.fontWeight,
        },
        border: { lineStyle: "none", stroke: "#000000", strokeWidth: 0, cornerRadius: 0 },
      } as unknown as SlideElement;
    }

    case "textStyle": {
      const s = draggedItem.data as TextStyle;
      return {
        id: "overlay",
        type: "text",
        position: { x: 0, y: 0 },
        size: { width: 338, height: 40 },
        content: s.name,
        style: {
          fontSize: s.fontSize ?? 16,
          fontWeight: s.fontWeight ?? undefined,
          color: s.color ?? "#000000",
          fontFamily: s.fontFamily ?? undefined,
          textAlign: s.textAlign,
          fontStyle: s.fontStyle ?? undefined,
          textDecoration: s.textDecoration ?? undefined,
        },
        border: {
          lineStyle: s.lineStyle ?? null,
          strokeWidth: s.strokeWidth ?? 0,
          stroke: s.stroke ?? "transparent",
          cornerRadius: s.cornerRadius ?? 0,
        },
      } as unknown as SlideElement;
    }

    case "question": {
      const template = questionTemplates[draggedItem.id as keyof typeof questionTemplates];
      if (!template) return null;
      return { ...template, id: "overlay", position: { x: 0, y: 0 } } as unknown as SlideElement;
    }

    case "image":
      if (!draggedItem.media) return null;
      return {
        id: "overlay",
        type: "image",
        position: { x: 0, y: 0 },
        size: imageSize ?? { width: 220, height: 220 },
        media: draggedItem.media,
        border: { lineStyle: "none", stroke: "black", strokeWidth: 0, cornerRadius: 12 },
      } as unknown as SlideElement;

    case "audio":
      if (!draggedItem.media) return null;
      return {
        id: "overlay",
        type: "audioPlayer",
        position: { x: 0, y: 0 },
        size: { width: 70, height: 70 },
        media: draggedItem.media,
        autoPlay: false,
        loop: false,
        border: noBorder,
      } as unknown as SlideElement;

    case "video":
      if (!draggedItem.media) return null;
      return {
        id: "overlay",
        type: "video",
        position: { x: 0, y: 0 },
        size: { width: 200, height: 200 },
        media: draggedItem.media,
        autoPlay: false,
        loop: false,
        border: noBorder,
      } as unknown as SlideElement;

    default:
      return null;
  }
}
